import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import {
  BookOpenIcon,
  FilesIcon,
  TextAaIcon,
  UsersIcon,
  KeyIcon,
  SignOutIcon,
} from '@phosphor-icons/react';
import type { Differentiation } from '@abgen/model';
import { api, ApiError, type ApiUser } from './api/client.js';
import { lastDocumentId, rememberDocument, useAutosave } from './hooks/useAutosave.js';
import { useEditorStore } from './state/editorStore.js';
import { useSelectionStore } from './state/selectionStore.js';
import { ChangePasswordDialog } from './components/ChangePasswordDialog.js';

import type { PrintMode } from './views/PrintView.js';

const DocumentListView = lazy(() =>
  import('./views/DocumentListView.js').then((module) => ({ default: module.DocumentListView })),
);
const PrintView = lazy(() =>
  import('./views/PrintView.js').then((module) => ({ default: module.PrintView })),
);
const ShareView = lazy(() =>
  import('./views/ShareView.js').then((module) => ({ default: module.ShareView })),
);
const PlayerView = lazy(() =>
  import('./views/PlayerView.js').then((module) => ({ default: module.PlayerView })),
);
const BoardView = lazy(() =>
  import('./views/BoardView.js').then((module) => ({ default: module.BoardView })),
);
const AccountsView = lazy(() =>
  import('./views/AccountsView.js').then((module) => ({ default: module.AccountsView })),
);
const EditorView = lazy(() =>
  import('./views/EditorView.js').then((module) => ({ default: module.EditorView })),
);
const FontsView = lazy(() =>
  import('./views/FontsView.js').then((module) => ({ default: module.FontsView })),
);
const LoginView = lazy(() =>
  import('./views/LoginView.js').then((module) => ({ default: module.LoginView })),
);

type Screen = 'laden' | 'anmelden' | 'liste' | 'editor' | 'schriften' | 'konten';

const LEVELS = ['basic', 'standard', 'advanced'];

export interface PrintRequest {
  documentId: string;
  mode: PrintMode;
  variants: number;
  variantIndex?: number;
  jobSeed: number;
  bleed: boolean;
  token?: string;
  /** Niveau-Variante (Phase 8.2). */
  level?: Differentiation;
}

/**
 * Druckansicht über die Adresse. Der PDF-Dienst ruft
 * `/print/<id>?mode=worksheet|solution|both&variant=<n>` auf (Phase 4.1); im
 * Editor genügt `?druck=<id>&variante=loesung`.
 */
export function printRequest(location = window.location): PrintRequest | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(location.search);
  const path = /^\/print\/([\w-]+)$/.exec(location.pathname);
  const documentId = path?.[1] ?? params.get('druck');
  if (!documentId) return null;

  const mode = params.get('mode') ?? params.get('variante');
  const variantIndex = Number(params.get('variant') ?? '');
  const token = params.get('token');
  return {
    documentId,
    mode:
      mode === 'solution' || mode === 'loesung'
        ? 'solution'
        : mode === 'both' || mode === 'beides'
          ? 'both'
          : 'worksheet',
    variants: Math.min(26, Math.max(1, Number(params.get('variants') ?? '1') || 1)),
    ...(Number.isInteger(variantIndex) && variantIndex >= 0 && params.get('variant')
      ? { variantIndex }
      : {}),
    jobSeed: Number(params.get('reroll') ?? '0') || 0,
    bleed: params.get('bleed') === '1',
    ...(LEVELS.includes(params.get('niveau') ?? '')
      ? { level: params.get('niveau') as Differentiation }
      : {}),
    ...(token ? { token } : {}),
  };
}

/** Geteiltes Blatt über die Adresse: `?freigabe=<token>&blatt=<id>` (Phase 4.4). */
export function shareRequest(
  location = window.location,
): { documentId: string; token: string } | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(location.search);
  const token = params.get('freigabe');
  const documentId = params.get('blatt');
  return token && documentId ? { documentId, token } : null;
}

/** Digitales Blatt über die Adresse: `/play/<code>` oder `?spielen=<code>`. */
export function playRequest(location = window.location): { code: string } | null {
  if (typeof window === 'undefined') return null;
  const path = /^\/play\/([A-Za-z0-9-]{4,12})$/.exec(location.pathname);
  const params = new URLSearchParams(location.search);
  const code = path?.[1] ?? params.get('spielen');
  return code ? { code: code.toUpperCase() } : null;
}

/** Tafelbild über die Adresse: `/board/<id>` oder `?tafel=<id>`. */
export function boardRequest(
  location = window.location,
): { documentId: string; token?: string } | null {
  if (typeof window === 'undefined') return null;
  const path = /^\/board\/([\w-]+)$/.exec(location.pathname);
  const params = new URLSearchParams(location.search);
  const documentId = path?.[1] ?? params.get('tafel');
  if (!documentId) return null;
  const token = params.get('token');
  return { documentId, ...(token ? { token } : {}) };
}

export function App() {
  return (
    <Suspense
      fallback={
        <p className="hint" role="status">
          Ansicht wird geladen …
        </p>
      }
    >
      <AppContent />
    </Suspense>
  );
}

function AppContent() {
  const play = playRequest();
  const board = boardRequest();
  const share = shareRequest();
  const print = printRequest();
  const [screen, setScreen] = useState<Screen>('laden');
  const [user, setUser] = useState<ApiUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const autosave = useAutosave(screen === 'editor' && !print && !share && !play && !board);

  const openDocument = useCallback(async (id: string) => {
    try {
      const result = await api.getDocument(id);
      useEditorStore.getState().load(result.document, result.id);
      // Rechtschreibprüfung für den Sprachraum des Dokuments vorbereiten.
      void import('./state/spellStore.js')
        .then(({ useSpellStore, loadPersonalDictionary }) => {
          void useSpellStore.getState().load(result.document.setup.locale);
          void loadPersonalDictionary();
        })
        .catch(() => undefined);
      useSelectionStore.getState().setPage(0);
      rememberDocument(id);
      setScreen('editor');
      setError(null);
    } catch (cause) {
      rememberDocument(null);
      setScreen('liste');
      setError(cause instanceof ApiError ? cause.message : 'Dokument nicht ladbar.');
    }
  }, []);

  // Sitzung prüfen und zuletzt geöffnetes Dokument wiederherstellen.
  useEffect(() => {
    if (print || share || play || board) return;
    let cancelled = false;
    void api
      .me()
      .then(async (result) => {
        if (cancelled) return;
        setUser(result.user);
        if (result.user.mustChangePassword) setPasswordOpen(true);
        const id = lastDocumentId();
        if (id) await openDocument(id);
        else setScreen('liste');
      })
      .catch(() => {
        if (!cancelled) setScreen('anmelden');
      });
    return () => {
      cancelled = true;
    };
  }, [openDocument, print, share, play, board]);

  const navigate = async (target: Screen) => {
    try {
      await autosave.saveNow();
      if (target === 'liste') rememberDocument(null);
      setError(null);
      setScreen(target);
    } catch {
      setError('Deine Änderungen konnten nicht gespeichert werden. Bitte versuche es erneut.');
    }
  };

  const signOut = async () => {
    try {
      await autosave.saveNow();
      await api.logout();
      rememberDocument(null);
      setUser(null);
      setError(null);
      setScreen('anmelden');
    } catch {
      setError('Abmelden fehlgeschlagen. Deine Sitzung bleibt geöffnet. Bitte versuche es erneut.');
    }
  };

  const toList = () => void navigate('liste');

  if (play) {
    return <PlayerView code={play.code} />;
  }

  if (board) {
    return (
      <BoardView documentId={board.documentId} {...(board.token ? { token: board.token } : {})} />
    );
  }

  if (share) {
    return <ShareView documentId={share.documentId} token={share.token} />;
  }

  if (print) {
    const { documentId, ...options } = print;
    return <PrintView documentId={documentId} {...options} />;
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <span className="brand-mark">
            <BookOpenIcon size={25} weight="duotone" aria-hidden="true" />
          </span>
          <div>
            <h1>Arbeitsblatt-Editor</h1>
            <span className="brand-caption">Raum für gute Lernideen</span>
          </div>
        </div>
        {user ? (
          <nav aria-label="Ansicht">
            <button
              type="button"
              aria-current={screen === 'liste' || screen === 'editor'}
              onClick={() => void navigate('liste')}
            >
              <FilesIcon size={19} aria-hidden="true" /> Arbeitsblätter
            </button>
            <button
              type="button"
              aria-current={screen === 'schriften'}
              onClick={() => void navigate('schriften')}
            >
              <TextAaIcon size={19} aria-hidden="true" /> Schriften
            </button>
            {user.role === 'admin' ? (
              <button
                type="button"
                aria-current={screen === 'konten'}
                onClick={() => void navigate('konten')}
              >
                <UsersIcon size={19} aria-hidden="true" /> Konten
              </button>
            ) : null}
          </nav>
        ) : null}
        {user ? (
          <p className="account">
            <span className="account-avatar" aria-hidden="true">
              {user.displayName.trim().charAt(0).toUpperCase()}
            </span>
            <span className="account-name">{user.displayName}</span>
            <button
              type="button"
              className="account-action"
              title="Passwort ändern"
              onClick={() => setPasswordOpen(true)}
            >
              <KeyIcon size={19} aria-hidden="true" />
              <span className="visually-hidden">Passwort</span>
            </button>
            <button
              type="button"
              className="account-action"
              title="Abmelden"
              onClick={() => void signOut()}
            >
              <SignOutIcon size={19} aria-hidden="true" />
              <span className="visually-hidden">Abmelden</span>
            </button>
          </p>
        ) : null}
      </header>

      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}

      {screen === 'laden' ? <p className="hint">Wird geladen …</p> : null}

      {screen === 'anmelden' ? (
        <LoginView
          onSignedIn={(signedIn) => {
            setUser(signedIn);
            if (signedIn.mustChangePassword) setPasswordOpen(true);
            setScreen('liste');
          }}
        />
      ) : null}

      {screen === 'liste' && user ? (
        <DocumentListView
          authorId={user.id}
          onOpen={(id) => void openDocument(id)}
          onCreated={(id) => void openDocument(id)}
        />
      ) : null}

      {screen === 'editor' ? (
        <EditorView
          onSave={autosave.saveNow}
          onClose={toList}
          saveState={autosave.state}
          saveError={autosave.error}
        />
      ) : null}

      {screen === 'schriften' ? <FontsView /> : null}

      {screen === 'konten' && user?.role === 'admin' ? <AccountsView me={user} /> : null}

      <ChangePasswordDialog
        open={passwordOpen}
        {...(user?.mustChangePassword ? { required: true } : {})}
        onClose={() => {
          if (!user?.mustChangePassword) setPasswordOpen(false);
        }}
        onChanged={() => {
          setUser((current) => (current ? { ...current, mustChangePassword: false } : current));
          setPasswordOpen(false);
        }}
      />
    </div>
  );
}
